import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { userAuthApi } from '../services/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tab මාරු කරන විට Form එක Clear කිරීම
  const toggleMode = (mode) => {
    setIsLogin(mode);
    setError('');
    setSuccess('');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setPhone('');
    setEmail('');
    setPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setError('');
    setSuccess('');

    if (!email || !password || (!isLogin && (!firstName || !lastName || !companyName))) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const response = await userAuthApi.login({ email, password });
        // Save minimal user info locally so other pages can autofill immediately
        try {
          if (response?.data?.user) {
            localStorage.setItem('currentUser', JSON.stringify(response.data.user));
          }
        } catch (e) {
          console.warn('Failed to save currentUser to localStorage', e);
        }
        const params = new URLSearchParams(location.search);
        const rawNext = params.get('next');
        const next = rawNext ? decodeURIComponent(rawNext) : '/';
        navigate(next);
      } else {
        await userAuthApi.register({ firstName, lastName, companyName, email, phone, password });
        toggleMode(true); // Register වූ පසු Login එකට මාරු කරයි
        setSuccess('Account created successfully! Please sign in.'); // Toggle වූ පසු Success Message එක සෙට් කිරීම
      }
    } catch (err) {
      if (!err.response) {
        setError('Unable to connect. Please check your internet connection and try again.');
      } else {
        setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#04050c] text-white">
      <Navbar />
      
      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4 py-12">
        <div className="w-full max-w-md rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060816] p-8 shadow-[0_0_40px_rgba(34,211,238,0.15)] relative overflow-hidden">
          
          {/* Background Glow */}
          <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[150px] blur-[80px] rounded-full pointer-events-none transition-all duration-500 ${isLogin ? 'bg-cyan-500/20' : 'bg-fuchsia-500/20'}`}></div>

          <div className="relative z-10">
            <div className="flex justify-center gap-6 mb-8 border-b border-white/10 pb-4">
              {isLogin ? (
                <h2 className="text-lg font-bold pb-2 px-2 relative text-cyan-400">
                  Sign In
                  <span className="absolute bottom-[-17px] left-0 w-full h-[2px] bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]"></span>
                </h2>
              ) : (
                <h2 className="text-lg font-bold pb-2 px-2 relative text-fuchsia-400">
                  Register
                  <span className="absolute bottom-[-17px] left-0 w-full h-[2px] bg-fuchsia-400 shadow-[0_0_10px_rgba(217,70,239,0.8)]"></span>
                </h2>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/40 text-green-300 text-sm">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                  <div className="grid gap-4 md:grid-cols-2 animate-fadeIn">
                    <div>
                      <label className="block text-slate-300 text-sm font-medium mb-2">First Name <span className="text-fuchsia-400">*</span></label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                        placeholder="John"
                        required={!isLogin}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-300 text-sm font-medium mb-2">Last Name <span className="text-fuchsia-400">*</span></label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                        placeholder="Doe"
                        required={!isLogin}
                      />
                    </div>
                  </div>
                  <div className="animate-fadeIn">
                    <label className="block text-slate-300 text-sm font-medium mb-2">Company Name <span className="text-fuchsia-400">*</span></label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                      placeholder="JOFFON L.L.C."
                      required={!isLogin}
                    />
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all ${isLogin ? 'focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]' : 'focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]'}`}
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              {!isLogin && (
                <div className="animate-fadeIn">
                  <label className="block text-slate-300 text-sm font-medium mb-2">Phone Number <span className="text-slate-500 font-normal">(Optional)</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              )}

              <div>
                <label className="block text-slate-300 text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all ${isLogin ? 'focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]' : 'focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]'}`}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center items-center gap-2 rounded-xl px-6 py-4 text-black font-bold transition-all duration-300 mt-4 text-lg disabled:opacity-70 disabled:cursor-not-allowed ${
                  isLogin 
                    ? 'bg-cyan-500 hover:bg-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.2)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)]' 
                    : 'bg-fuchsia-500 hover:bg-fuchsia-400 shadow-[0_0_20px_rgba(217,70,239,0.2)] hover:shadow-[0_0_25px_rgba(217,70,239,0.4)]'
                }`}
              >
                {loading && (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                )}
                {loading 
                  ? (isLogin ? 'Signing In...' : 'Creating Account...') 
                  : (isLogin ? 'Sign In' : 'Create Account')}
              </button>
              
              <div className="text-center text-sm text-slate-400 pt-2">
                {isLogin ? (
                  <>
                    Don't have an account?{' '}
                    <button type="button" onClick={() => toggleMode(false)} className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors">
                      Register here
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button type="button" onClick={() => toggleMode(true)} className="text-fuchsia-400 hover:text-fuchsia-300 font-medium transition-colors">
                      Sign in here
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;